import React, { useState, useEffect } from 'react';
import { Card, Text, Button, Group, Tabs } from '@mantine/core';
import api, { Book } from '../services/api';

function BookList() {
  const [books, setBooks] = useState<Book[]>([]);
  const [reservedBooks, setReservedBooks] = useState<Book[]>([]);
  const [userId, setUserId] = useState<number | null>(null);

  useEffect(() => {
    const loadBooks = async () => {
      const [allBooks, user] = await Promise.all([
        api.books.getAll(),
        api.telegram.getUser()
      ]);
      
      if (user) {
        setUserId(user.id);
        const reserved = await api.books.getReservedByUser(user.id);
        const reservedWithId = reserved.map(book => ({
          ...book,
          reserverId: user.id
        }));
        setReservedBooks(reservedWithId);

        // Обновляем reserverId в общем списке книг
        const updatedAllBooks = allBooks.map(book => {
          const reservedBook = reservedWithId.find(r => r.id === book.id);
          return reservedBook ? { ...book, reserverId: user.id } : book;
        });
        setBooks(updatedAllBooks);
      } else {
        setBooks(allBooks);
      }
    };
    loadBooks();
  }, []);

  const handleReserve = async (bookId: number) => {
    try {
      if (!api.telegram.isInitialized()) {
        throw new Error('Приложение должно быть открыто в Telegram');
      }
      
      const user = api.telegram.getUser();
      if (!user) {
        throw new Error('Пожалуйста, откройте приложение через Telegram');
      }
      
      await api.books.reserve(bookId, user.id);
      const updatedBook = { ...books.find(b => b.id === bookId)!, available: false, reserverId: user.id };
      setBooks(books.map(book => 
        book.id === bookId ? updatedBook : book
      ));
      setReservedBooks([...reservedBooks, updatedBook]);
      api.telegram.showAlert('Книга успешно забронирована!');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Ошибка при бронировании';
      api.telegram.showAlert(message);
    }
  };

  const handleUnReserve = async (bookId: number) => {
    try {
      if (!api.telegram.isInitialized()) {
        throw new Error('Приложение должно быть открыто в Telegram');
      }
      
      const user = api.telegram.getUser();
      if (!user) {
        throw new Error('Пожалуйста, откройте приложение через Telegram');
      }
      
      await api.books.unreserve(bookId, user.id);
      const updatedBook = { ...books.find(b => b.id === bookId)!, available: true, reserverId: undefined };
      setBooks(books.map(book => 
        book.id === bookId ? updatedBook : book
      ));
      setReservedBooks(reservedBooks.filter(book => book.id !== bookId));
      api.telegram.showAlert('Книга успешно возвращена!');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Ошибка при возврате';
      api.telegram.showAlert(message);
    }
  };

  const getButtonProps = (book: Book) => {
    if (book.available) {
      return {
        color: "blue" as const,
        text: "Забронировать",
        onClick: () => handleReserve(book.id),
        disabled: false
      };
    } else if (book.reserverId === userId) {
      return {
        color: "red" as const,
        text: "Вернуть",
        onClick: () => handleUnReserve(book.id),
        disabled: false
      };
    } else {
      return {
        color: "gray" as const,
        text: "Недоступно",
        onClick: undefined,
        disabled: true
      };
    }
  };

  const renderBookList = (booksToRender: Book[]) => (
    <div className="book-list">
      {booksToRender.map((book) => {
        const buttonProps = getButtonProps(book);
        return (
          <Card key={book.id} shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text fw={500}>{book.title}</Text>
            </Group>
            <Text size="sm" c="dimmed">
              {book.description}
            </Text>
            <Button
              variant="light"
              color={buttonProps.color}
              fullWidth
              mt="md"
              radius="md"
              onClick={buttonProps.onClick}
              disabled={buttonProps.disabled}
            >
              {buttonProps.text}
            </Button>
          </Card>
        );
      })}
    </div>
  );

  return (
    <Tabs defaultValue="all">
      <Tabs.List>
        <Tabs.Tab value="all">Все книги</Tabs.Tab>
        <Tabs.Tab value="reserved">Мои книги</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="all" pt="xs">
        {renderBookList(books)}
      </Tabs.Panel>

      <Tabs.Panel value="reserved" pt="xs">
        {renderBookList(reservedBooks)}
      </Tabs.Panel>
    </Tabs>
  );
}

export default BookList; 